import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Award, TrendingUp, Mic, Building2, Users,
  FileText, ArrowRight, CheckCircle } from
"lucide-react";
import { Button } from "@/components/ui/button";

const achievements = [
{
  year: "2024",
  title: "AI Patents Filed",
  description: "Filed multiple patents in conversational AI, intelligent routing, and automated agent assist technologies.",
  icon: FileText,
  category: "Innovation"
},
{
  year: "2023-2026",
  title: "$100M+ Contact Center Modernization and Cloud Adoption Programs",
  description: "Led large-scale contact center transformation programs, implementing Gen AI and Agentic AI solutions across enterprise operations.",
  icon: Building2,
  category: "Leadership"
},
{
  year: "2025",
  title: "AI-Powered Agent Assist Launch",
  description: "Designed and deployed production-grade agent assist systems, reducing average handle time by 25% and improving first-call resolution.",
  icon: TrendingUp,
  category: "Impact"
},
{
  year: "2024-2025",
  title: "Voice AI Modernization",
  description: "Transformed legacy IVR systems into intelligent voice AI platforms with natural language understanding and predictive routing.",
  icon: Mic,
  category: "Transformation"
},
{
  year: "2023-2024",
  title: "ML Automation at Scale",
  description: "Built enterprise ML pipelines and MLOps infrastructure, enabling continuous model training and deployment for multiple business units.",
  icon: TrendingUp,
  category: "Technology"
},
{
  year: "2023-2024",
  title: "Executive AI Education Program",
  description: "Developed and delivered AI literacy programs for executive leadership, enabling informed decision-making on AI investments.",
  icon: Users,
  category: "Education"
},
{
  year: "2022-2023",
  title: "Enterprise AI Strategy Lead",
  description: "Defined comprehensive AI strategy and roadmap for Fortune 500 company, aligning AI initiatives with business objectives.",
  icon: Award,
  category: "Strategy"
}];


const metrics = [
{ value: "$100M+", label: "Modernization Programs Led" },
{ value: "25%", label: "Handle Time Reduction" },
{ value: "3+", label: "AI Patents Filed" },
{ value: "1000+", label: "Leaders Trained" }];


export default function AchievementsPage() {
  return (
    <div className="pt-20" data-testid="achievements-page">
      {/* Hero Section */}
      <section className="py-24 md:py-32 hero-gradient grain">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl">

            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-royal-blue-800 mb-4 block">
              Track Record
            </span>
            <h1 className="font-display text-4xl md:text-5xl font-bold text-zinc-950 mb-6">
              Achievements & Accomplishments
            </h1>
            <p className="text-lg text-zinc-600 leading-relaxed">
              A track record of delivering measurable impact through AI transformation, 
              from patents to production systems to executive education.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Key Metrics Section */}
      <section className="py-16 bg-zinc-950" data-testid="metrics-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {metrics.map((metric, index) =>
            <motion.div
              key={metric.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="text-center"
              data-testid={`metric-${index}`}>

                <div className="font-display text-4xl md:text-5xl font-bold text-white mb-2">
                  {metric.value}
                </div>
                <div className="text-zinc-400 text-sm">
                  {metric.label}
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-24 md:py-32 bg-white" data-testid="timeline-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-16">

            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-royal-blue-800 mb-4 block">
              Journey
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-zinc-950 mb-4">
              Career Milestones
            </h2>
          </motion.div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-0 md:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-royal-blue-800 via-royal-blue-600 to-royal-blue-400 transform md:-translate-x-1/2"></div>

            <div className="space-y-12">
              {achievements.map((achievement, index) =>
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`relative flex flex-col md:flex-row gap-8 ${
                index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`
                }
                data-testid={`achievement-${index}`}>

                  {/* Timeline Dot */}
                  <div className="absolute left-0 md:left-1/2 top-0 transform md:-translate-x-1/2 -translate-x-1/2">
                    <div className="timeline-dot"></div>
                  </div>

                  {/* Content */}
                  <div className={`flex-1 pl-8 md:pl-0 ${index % 2 === 0 ? 'md:pr-16 md:text-right' : 'md:pl-16'}`}>
                    <div className={`inline-block ${index % 2 === 0 ? 'md:ml-auto' : ''}`}>
                      <span className="inline-block px-3 py-1 bg-royal-blue-100 text-royal-blue-800 text-xs font-semibold tracking-wider uppercase rounded-full mb-3">
                        {achievement.category}
                      </span>
                    </div>
                    <div className="!text-sm mb-2 text-zinc-500">{achievement.year}</div>
                    <h3 className="font-display md:text-2xl !font-semibold !text-xl mb-3 text-zinc-950">
                      {achievement.title}
                    </h3>
                    <p className="text-zinc-600">
                      {achievement.description}
                    </p>
                  </div>

                  {/* Icon */}
                  <div className={`hidden md:flex flex-1 items-start ${index % 2 === 0 ? 'md:pl-16' : 'md:pr-16 justify-end'}`}>
                    <div className="skill-icon w-16 h-16">
                      <achievement.icon size={32} />
                    </div>
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Recognition Section */}
      <section className="py-24 md:py-32 section-muted" data-testid="recognition-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16">

            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-royal-blue-800 mb-4 block">
              Recognition
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-zinc-950 mb-4">
              Speaking & Thought Leadership
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
            { title: "AI Executive Workshops", desc: "Led workshops on AI strategy for C-suite executives" },
            { title: "Industry Conferences", desc: "Speaker at enterprise AI and contact center events" },
            { title: "Internal Training Programs", desc: "Developed AI literacy curriculum for technical teams" },
            { title: "Technical White Papers", desc: "Authored papers on RAG systems and voice AI" },
            { title: "Podcast Appearances", desc: "Guest on AI and technology leadership podcasts" },
            { title: "Mentorship", desc: "Mentoring next-generation AI leaders and practitioners" }].
            map((item, index) =>
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="p-6 bg-white border border-zinc-200 rounded-sm"
              data-testid={`recognition-${index}`}>

                <CheckCircle className="text-royal-blue-800 mb-4" size={24} />
                <h3 className="font-display text-lg font-semibold text-zinc-950 mb-2">
                  {item.title}
                </h3>
                <p className="text-zinc-600 text-sm">
                  {item.desc}
                </p>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 cta-section" data-testid="achievements-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}>

            <h2 className="font-display text-3xl md:text-4xl font-semibold mb-6">
              Let's Create Impact Together
            </h2>
            <p className="text-blue-100 text-lg mb-8 max-w-xl mx-auto">
              Ready to transform your enterprise with AI? Let's discuss how we can achieve similar results for your organization.
            </p>
            <Link to="/contact">
              <Button
                className="bg-white text-royal-blue-800 hover:bg-blue-50 px-8 py-6 text-xs tracking-wider uppercase"
                data-testid="achievements-cta-btn">

                Start a Conversation
                <ArrowRight className="ml-2" size={16} />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>);

}